import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Marque } from './Marque';
import './Connexion.css';

/**
 * La connexion et l'inscription posées sur la vitrine (demande d'Igor du 2026-10-21 : « on
 * arrive directement sur la vitrine et c'est juste la partie de connexion droite qui pop par
 * dessus ») : la vitrine (`/vitrine/`) reste derrière, voilée, et la feuille scotchée de la
 * maquette des mails (`.scratch/chantiers/mails/maquettes/connexion.html`) apparaît au centre.
 * Un clic à côté, la croix ou Échap la referment : on retourne à la vitrine.
 */
export function AccueilSansCompte({
  titre,
  description,
  centre = false,
  children,
}: {
  titre: string;
  /** Vide : pas de phrase sous le titre, l'écran pose la sienne. */
  description: string;
  /** Les feuilles d'un seul message (« Regarde ta boîte mail », « Adresse vérifiée ») : centrées. */
  centre?: boolean;
  children: ReactNode;
}) {
  const fermer = () => window.location.assign('/vitrine/');
  useEffect(() => {
    const echap = (e: KeyboardEvent) => {
      if (e.key === 'Escape') fermer();
    };
    window.addEventListener('keydown', echap);
    return () => window.removeEventListener('keydown', echap);
  }, []);

  return (
    <div className="connexion">
      <iframe className="connexion-fond" src="/vitrine/" title="Nextilt" tabIndex={-1} aria-hidden="true" />
      <div className="connexion-voile" onClick={fermer} aria-hidden="true" />

      <div className={centre ? 'connexion-feuille connexion-feuille--centre' : 'connexion-feuille'} role="dialog" aria-modal="true" aria-labelledby="connexion-titre">
        <button type="button" className="connexion-fermer" onClick={fermer} aria-label="Revenir à la présentation">
          ×
        </button>
        <div className="connexion-marque">
          <Marque taille={28} />
          Nextilt
        </div>
        <h2 className="connexion-titre" id="connexion-titre">
          {titre}
        </h2>
        {description !== '' && <p className="connexion-description">{description}</p>}
        {children}
        {/* Ticket 05 : une phrase d'information, pas une case à cocher. La base légale est le
            service rendu, rien n'est à accepter. */}
        <p className="connexion-regles">
          Ce que Nextilt garde sur toi&nbsp;: <a href="/confidentialite">Confidentialité</a>
          {' · '}
          <a href="/mentions-legales">Mentions légales</a>
        </p>
      </div>
    </div>
  );
}

/**
 * La ligne du mot de passe : l'espacement des points, et l'œil qui le montre. `current-password`
 * à la connexion, `new-password` à l'inscription, pour que le téléphone propose d'en tirer un.
 */
export function ChampMotDePasse({
  id,
  valeur,
  onChange,
  autoComplete,
  autoFocus = false,
}: {
  id: string;
  valeur: string;
  onChange: (valeur: string) => void;
  autoComplete: 'current-password' | 'new-password';
  autoFocus?: boolean;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="connexion-ligne">
      <input
        id={id}
        className="connexion-saisie"
        type={visible ? 'text' : 'password'}
        value={valeur}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        spellCheck={false}
        maxLength={512}
      />
      <button
        type="button"
        className="connexion-oeil"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'masquer le mot de passe' : 'montrer le mot de passe'}
      >
        <Oeil barre={visible} />
      </button>
    </div>
  );
}

/** Le champ e-mail : clavier e-mail au téléphone, sans majuscule ni correction automatiques. */
export function ChampEmail({
  id,
  valeur,
  onChange,
  sansFocus = false,
}: {
  id: string;
  valeur: string;
  onChange: (valeur: string) => void;
  /** L'inscription commence par le code : l'e-mail n'y prend pas le curseur. */
  sansFocus?: boolean;
}) {
  return (
    <div className="connexion-ligne">
      <input
        id={id}
        className="connexion-saisie connexion-saisie--texte"
        type="email"
        inputMode="email"
        value={valeur}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="username"
        autoCapitalize="none"
        autoCorrect="off"
        autoFocus={!sansFocus}
        spellCheck={false}
        maxLength={320}
      />
    </div>
  );
}

function Oeil({ barre }: { barre: boolean }) {
  return (
    <svg width="17" height="17" viewBox="0 0 18 18" fill="none">
      <path
        d="M1.4 9S4.4 3.4 9 3.4 16.6 9 16.6 9 13.6 14.6 9 14.6 1.4 9 1.4 9z"
        stroke="currentColor"
        strokeWidth="1.3"
      />
      <circle cx="9" cy="9" r="2.2" stroke="currentColor" strokeWidth="1.3" />
      {barre && <path d="M2.5 15.5L15.5 2.5" stroke="currentColor" strokeWidth="1.3" />}
    </svg>
  );
}
